import React from 'react';
import clsx from 'clsx';
import { Alert } from '@dopomogai/ui'; 
import { ArrowRight, Maximize2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';

export type ArticleBlock = 
  | { type: 'paragraph'; text: string }
  | { type: 'image'; url: string; alt: string; caption?: string }
  | { type: 'list'; style: 'unordered' | 'ordered'; items: string[] }
  | { type: 'callout'; variant: 'info' | 'quote' | 'warning'; text: string }
  | { type: 'code'; language: string; code: string }
  | { type: 'cta'; title: string; text: string; buttonText: string; buttonLink: string };

type ArticleImageClickPayload = {
  src: string;
  alt: string;
  caption?: string;
};

const MarkdownText: React.FC<{ text: string }> = ({ text }) => (
  <ReactMarkdown 
    components={{
      p: ({node, ...props}) => <span {...props} />,
      strong: ({node, ...props}) => <strong className="font-extrabold text-slate-900" {...props} />,
      em: ({node, ...props}) => <em className="italic text-slate-800" {...props} />,
      a: ({node, ...props}) => <a className="text-emerald-600 hover:underline font-medium" {...props} />
    }}
  >
    {text}
  </ReactMarkdown>
);

export const ArticleBlockRenderer: React.FC<{
  block: ArticleBlock;
  onImageClick?: (image: ArticleImageClickPayload) => void;
}> = ({ block, onImageClick }) => {
  switch (block.type) {
    case 'paragraph':
      return (
        <div className="text-[1.02rem] md:text-[1.1rem] text-slate-700 leading-8 md:leading-9 mb-7">
          <MarkdownText text={block.text} />
        </div>
      );

    case 'image':
      return (
        <figure className="my-10 md:my-14">
          {onImageClick ? (
            <button
              type="button"
              onClick={() =>
                onImageClick({
                  src: block.url,
                  alt: block.alt,
                  caption: block.caption,
                })
              }
              className="group block w-full cursor-zoom-in text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-4"
              aria-label={`Open image fullscreen: ${block.alt}`}
            >
              <div className="relative rounded-xl overflow-hidden border-2 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] bg-slate-50">
                <img
                  src={block.url}
                  alt={block.alt}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="pointer-events-none absolute right-4 top-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-slate-950/70 px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-white opacity-0 backdrop-blur transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100">
                  <Maximize2 size={12} />
                  Full screen
                </div>
              </div>
            </button>
          ) : (
            <div className="rounded-xl overflow-hidden border-2 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] bg-slate-50">
              <img
                src={block.url}
                alt={block.alt}
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          )}
          {block.caption && (
            <figcaption className="mt-4 text-center text-sm font-medium text-slate-500 italic">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );

    case 'list':
      if (block.style === 'ordered') {
        return (
          <ol className="mb-10 space-y-4">
            {block.items.map((item, i) => (
              <li
                key={i}
                className="grid grid-cols-[auto,1fr] items-start gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
              >
                <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-black bg-white text-sm font-black text-slate-900 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                  {i + 1}
                </span>
                <div className="text-[1rem] md:text-[1.06rem] text-slate-700 leading-8">
                  <MarkdownText text={item} />
                </div>
              </li>
            ))}
          </ol>
        );
      }

      return (
        <ul className="mb-10 ml-3 md:ml-5 space-y-4">
          {block.items.map((item, i) => (
            <li key={i} className="relative pl-6 md:pl-7">
              <span className="absolute left-0 top-[0.72rem] h-2.5 w-2.5 rounded-full border border-black bg-emerald-500" />
              <div className="text-[0.98rem] md:text-[1.03rem] text-slate-700 leading-7 md:leading-8">
                <MarkdownText text={item} />
              </div>
            </li>
          ))}
        </ul>
      );

    case 'callout':
      if (block.variant === 'quote') {
        return (
          <blockquote className="my-12 rounded-r-2xl border-l-4 border-emerald-500 bg-emerald-50/40 pl-6 pr-4 py-4 md:pl-8">
            <div className="mb-3 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700">
              Key line
            </div>
            <div className="text-2xl md:text-[2rem] font-bold italic leading-tight text-slate-900">
              <MarkdownText text={block.text} />
            </div>
          </blockquote>
        );
      }
      return (
        <div className="my-8">
          <Alert 
            type={block.variant === 'warning' ? 'error' : 'info'} 
            message={<MarkdownText text={block.text} />} 
            className="border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
          />
        </div>
      );
      
    case 'code':
      return (
        <div className="my-8 bg-slate-900 rounded-xl p-6 overflow-x-auto border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 border-b border-slate-700 pb-2">
                {block.language}
            </div>
            <pre className="text-sm text-emerald-300 font-mono leading-relaxed">{block.code}</pre>
        </div>
      );

    case 'cta':
      // Shared classes for both Link (internal) and a (external)
      const buttonClasses = "w-full md:w-auto inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white border-2 border-black rounded-lg px-6 py-3 font-bold shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all cursor-pointer no-underline";
      
      const isInternal = block.buttonLink && block.buttonLink.startsWith('/');

      return (
        <div className="my-12 p-8 bg-emerald-50 border-2 border-black rounded-xl shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
                <h3 className="text-2xl font-extrabold text-slate-900 mb-2 leading-tight">{block.title}</h3>
                <p className="text-lg text-slate-700 font-medium">{block.text}</p>
            </div>
            <div className="shrink-0 w-full md:w-auto">
                {isInternal ? (
                  <Link to={block.buttonLink} className={buttonClasses}>
                    {block.buttonText} <ArrowRight size={20} />
                  </Link>
                ) : (
                  <a href={block.buttonLink} className={buttonClasses}>
                    {block.buttonText} <ArrowRight size={20} />
                  </a>
                )}
            </div>
        </div>
      );

    default:
      return null;
  }
};
